import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Globe, 
  MapPin, 
  Phone, 
  FileText,
  Users,
  CheckCircle,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { authService } from '../../services/auth.service';
import categoriesService, { Category } from '../../services/categories.service';
import logo from '../../assets/logo.png';

interface CompleteProfileData {
  about?: string;
  address?: string;
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  country_id?: number;
  wilaya_id?: number;
  category_ids: string[];
}

interface FormErrors {
  about?: string;
  address?: string;
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  country_id?: string;
  wilaya_id?: string;
  category_ids?: string;
}

const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CompleteProfileData>({
    about: '',
    address: '',
    website: '',
    facebook_url: '',
    linkedin_url: '',
    country_id: 1, // Default to Algeria
    wilaya_id: undefined,
    category_ids: []
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await categoriesService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'wilaya_id' || name === 'country_id') ? parseInt(value) || undefined : value
    }));
    // Clear error when user starts typing
    if (name in errors) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.category_ids || formData.category_ids.length === 0) {
      newErrors.category_ids = 'Veuillez sélectionner au moins une catégorie d\'expertise';
    }

    if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Veuillez entrer une URL valide (ex: https://example.com)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await authService.updateTrainingCenter(formData);
      navigate('/training-center/dashboard');
    } catch (error: any) {
      setErrors({ about: error.message || 'Erreur lors de la mise à jour du profil. Veuillez réessayer.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // Allow users to skip this step and complete later
    navigate('/training-center/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src={logo} alt="PHINLEX" className="h-8 w-auto" />
          <span className="ml-2 text-xl font-serif font-bold text-gray-900">PHINLEX</span>
        </div>

        {/* Profile Completion Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border border-black/5 p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-brand-accent" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Finalisez votre profil
            </h1>
            <p className="text-gray-600">
              Complétez les informations de votre centre de formation pour améliorer votre visibilité
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Étape 2 sur 2</span>
              <span>Informations détaillées</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-brand-accent h-2 rounded-full w-full"></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* About */}
            <div>
              <label htmlFor="about" className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-2" />
                À propos de votre centre
              </label>
              <textarea
                id="about"
                name="about"
                rows={4}
                value={formData.about}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors resize-none"
                placeholder="Décrivez votre centre de formation, votre mission, vos valeurs..."
              />
              {errors.about && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.about}
                </p>
              )}
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline h-4 w-4 mr-2" />
                Adresse complète
              </label>
              <input
                id="address"
                name="address"
                type="text"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
                placeholder="Adresse complète de votre centre"
              />
            </div>

            {/* Country Selection */}
            <div>
              <label htmlFor="country_id" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline h-4 w-4 mr-2" />
                Pays
              </label>
              <select
                id="country_id"
                name="country_id"
                value={formData.country_id || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
              >
                <option value="1">Algérie</option>
                <option value="2">France</option>
                <option value="3">Maroc</option>
                <option value="4">Tunisie</option>
                <option value="5">Canada</option>
                <option value="6">Belgique</option>
                <option value="7">Suisse</option>
              </select>
            </div>

            {/* Wilaya Selection */}
            <div>
              <label htmlFor="wilaya_id" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline h-4 w-4 mr-2" />
                Wilaya (Province)
              </label>
              <select
                id="wilaya_id"
                name="wilaya_id"
                value={formData.wilaya_id || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
              >
                <option value="">Sélectionnez une wilaya</option>
                <option value="16">Alger</option>
                <option value="31">Oran</option>
                <option value="25">Constantine</option>
                <option value="6">Béjaïa</option>
                <option value="9">Blida</option>
                <option value="19">Sétif</option>
                <option value="22">Skikda</option>
                <option value="24">Annaba</option>
                <option value="26">Guelma</option>
                <option value="27">Médéa</option>
                <option value="29">Mostaganem</option>
                <option value="32">El Bayadh</option>
                <option value="34">Bordj Bou Arréridj</option>
                <option value="35">Boumerdès</option>
                <option value="36">El Tarf</option>
                <option value="37">Tindouf</option>
                <option value="38">Tissemsilt</option>
                <option value="39">El Oued</option>
                <option value="40">Khenchela</option>
                <option value="41">Souk Ahras</option>
                <option value="42">Tipaza</option>
                <option value="43">Mila</option>
                <option value="44">Aïn Defla</option>
                <option value="45">Naâma</option>
                <option value="46">Aïn Témouchent</option>
                <option value="47">Ghardaïa</option>
                <option value="48">Relizane</option>
              </select>
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="inline h-4 w-4 mr-2" />
                Site web
              </label>
              <input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
                placeholder="https://votre-site.com"
              />
              {errors.website && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.website}
                </p>
              )}
            </div>

            {/* Facebook */}
            <div>
              <label htmlFor="facebook_url" className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline h-4 w-4 mr-2" />
                Facebook
              </label>
              <input
                id="facebook_url"
                name="facebook_url"
                type="url"
                value={formData.facebook_url}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
                placeholder="https://facebook.com/votre-page"
              />
            </div>

            {/* LinkedIn */}
            <div>
              <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline h-4 w-4 mr-2" />
                LinkedIn
              </label>
              <input
                id="linkedin_url"
                name="linkedin_url"
                type="url"
                value={formData.linkedin_url}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
                placeholder="https://linkedin.com/company/votre-entreprise"
              />
            </div>

            {/* Categories of Expertise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <CheckCircle className="inline h-4 w-4 mr-2" />
                Catégories d'expertise *
              </label>
              <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-4">
                {loadingCategories ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-accent"></div>
                    <span className="ml-2 text-sm text-gray-500">Chargement des catégories...</span>
                  </div>
                ) : (
                  categories.map(category => (
                    <label key={category.id} className="flex items-center space-x-3 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.category_ids.includes(category.id)}
                        onChange={() => handleCategoryChange(category.id)}
                        className="rounded border-gray-300 text-brand-accent focus:ring-brand-accent focus:ring-2"
                      />
                      <span>{category.name_french}</span>
                    </label>
                  ))
                )}
              </div>
              {errors.category_ids && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.category_ids}
                </p>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6">
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Passer cette étape
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-brand-accent text-white py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Finalisation...
                  </>
                ) : (
                  <>
                    Finaliser mon profil
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Vous pourrez modifier ces informations à tout moment depuis votre tableau de bord
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
